'use client'
import * as React from 'react'
import { cubicBezier, motion, useTransform, AnimatePresence } from 'framer-motion'
import { useConstant } from 'hooks/use-constant'
import { useLinearProgress } from 'hooks/use-linear-progress'
import { randomBetween } from 'utils/math'

import { CoinIcon, StarCircleTipIcon } from './../../../assets/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { useNotEnoughCoinsDialog } from '@repo/web-ui/ask-cf/contexts/not-enough-coins-dialog-context'

export interface TipButtonProps {
  coinsCount?: number | null
  size?: 'small' | 'regular'
  isLoading?: boolean
  onTip: () => void
  disabled?: boolean
}

export function TipButton({
  coinsCount,
  size = 'small',
  isLoading = false,
  onTip,
  disabled = false,
}: TipButtonProps) {
  const spread = 350
  const duration = 1250
  const [coins, setCoins] = React.useState<number[]>([])
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const userBalanceUserQuery = useUserCoinBalanceQuery()
  const { openDialog } = useNotEnoughCoinsDialog()

  function onAnimate() {
    const next = [
      randomBetween(0, 0.1),
      randomBetween(0, 0.2),
      ...Array(5 - 2)
        .fill(0)
        .map(() => randomBetween(0, spread)),
    ]

    setCoins(coins => [...coins, ...next])

    setTimeout(() => {
      setCoins(coins => coins.filter(coin => !next.includes(coin)))
    }, duration + spread)
  }

  const balance = React.useMemo(
    () => userBalanceUserQuery.data?.userBalance.balance || 0,
    [userBalanceUserQuery.data?.userBalance.balance],
  )

  const notEnoughCoins = Boolean(balance < 5)

  function handleClickGiveTip() {
    if (!isLoggedInQuery.isPending && !isLoggedInQuery.data) {
      openModal('register')
      return
    } else if (notEnoughCoins) {
      openDialog()
    } else {
      onAnimate()
      onTip()
    }
  }

  const isDisabledButton = disabled || isLoading

  return (
    <div className="tablet:inline-block !cursor-pointer flex items-center">
      <div className="text-cf-sf-gray-300 relative z-[100]">
        <AnimatePresence>
          {coins.map(delay => (
            <CoinsAnimation key={delay} delay={delay} duration={duration} />
          ))}
        </AnimatePresence>
      </div>
      <Button
        variant="ghost"
        size={'small'}
        className={tw(
          'text-cf-violet  border pointer-events-auto !cursor-pointer !disabled:cursor-pointer font-medium border-[#1313151F] flex w-auto bg-cf-grey-50 flex-row items-center p-0 !px-2.5 rounded-[40px] hover:bg-cf-grey-200 hover:text-cf-violet',
          size === 'regular' ? 'h-[32px] !px-3 text-16' : 'h-[26px] !px-2.5 text-14',
          typeof coinsCount === 'number' ? 'gap-1' : 'gap-0',
        )}
        disabled={isDisabledButton}
        iconLeft={<StarCircleTipIcon className={tw(size === 'regular' ? '!size-5' : '!size-4')} />}
        onClick={handleClickGiveTip}
        data-testid="tip-button"
        aria-label="Give tip 5 coins button"
      >
        {typeof coinsCount === 'number' ? coinsCount : null}
      </Button>
    </div>
  )
}

export function CoinsAnimation({ delay, duration }: { delay: number; duration: number }) {
  const offsetX = useConstant(() => randomBetween(-150, 150))
  const rotateXTo = useConstant(() => randomBetween(-360, 360))
  const bottom = useConstant(() => randomBetween(20, 60))

  const values = useLinearProgress({
    duration,
    from: 0,
    to: 1,
    delay,
  })

  const eased = useTransform(values.value, cubicBezier(0, 1, 0.5, 2))
  const y = useTransform(eased, value => -bottom * value)
  const x = useTransform(values.value, value => `${value * offsetX}%`)
  const opacity = useTransform(values.value, [0, 0.1, 0.6, 1], [0, 1, 1, 0])
  const rotateZ = useTransform(values.value, value => `${value * rotateXTo}deg`)

  return (
    <motion.div
      className="absolute left-[40%] top-0 z-[100] origin-center saturate-200"
      style={{ x, y, opacity, rotateZ }}
    >
      <CoinIcon className="text-cf-sf-gray-300" />
    </motion.div>
  )
}
